import { type LoyaltyUserRegistrationInfo } from '@koala/sdk';
import { Form, Formik } from 'formik';
import { boolean, lazy, object, string } from 'yup';
import { StyledSubmitButtonRow } from './styles';
import { SignupBonusBadge } from './signup-bonus-badge';
import { styled } from '@/components/content/stitches';
import { StyledCheckbox } from '@/components/uielements/checkbox/styles';
import { StyledPhoneDisclosure } from '@/components/checkout/account/styles';
import { StyledFormField } from '@/components/uielements/form-fields/styles';
import { Input } from '@/components/uielements/formik';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { SIGNUP_OPTIONAL_FIELDS } from '@/constants/authentication';
import { REGEX, REGEX_VALIDATION_STRINGS, VALIDATION_STRINGS } from '@/constants/validation';
import { useSelector } from '@/redux';
import { getSignupOptionalFields } from '@/utils/auth';
import { replaceHrefWithWord } from '@/utils/urls';
import { LINKS_MAP } from '@/constants/global';
import { Render } from '@/components/uielements/render';
import { ConnectionErrorState } from '@/components/connectionError';

interface Props {
  loading: boolean;
  loadingCaptcha?: boolean;
  connectionError?: boolean;
  /**
   * True from the moment a valid submit starts (captcha fetch + register + chained login)
   * until the flow resolves. Drives the primary button's disabled state so the form cannot
   * be double-submitted at checkout, where the Redux `loading` flag is cleared mid-flight
   * (TEAM-13451).
   */
  submitting?: boolean;
  doSignUp: (values: SignUpFormFieldValues) => void;
}

export interface SignUpFormFieldValues extends Partial<LoyaltyUserRegistrationInfo> {
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  invite_code?: string;
  birthday?: string;
  terms_and_conditions: boolean;
  opt_in?: boolean;
}

export function SignUpForm({
  doSignUp,
  loading,
  loadingCaptcha,
  connectionError,
  submitting,
}: Props) {
  const organization = useSelector((state) => state.app.organization);
  const { strings, webConfig } = useSelector((state) => state.app.cmsConfig);
  const loyaltyDriverID = organization.organization.loyalty_driver_id;
  const brandName = organization.organization.label;
  let acceptTerms = replaceHrefWithWord(
    strings.signup.accept_terms_conditions ?? '',
    'terms',
    LINKS_MAP.terms,
  );
  acceptTerms = replaceHrefWithWord(acceptTerms, 'privacy', LINKS_MAP.privacy).replace(
    '$brandName$',
    brandName,
  );

  const isEnabled = (field: SIGNUP_OPTIONAL_FIELDS) => {
    const registrationFields = organization.external_auth.registration_fields.user;
    const optionalFields = getSignupOptionalFields(loyaltyDriverID);
    // @ts-expect-error field can't index `accounts`.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const cmsConfigFieldValue = webConfig.accounts[field];

    if (!registrationFields?.includes(field) && !optionalFields?.includes(field)) {
      return false;
    }

    // Check CMS config
    if (cmsConfigFieldValue === false || cmsConfigFieldValue === 'disabled') {
      return false;
    }

    return true;
  };

  const initialValues: SignUpFormFieldValues = {
    first_name: '',
    // honeypot input field to prevent bots, should always be empty
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    invite_code: undefined,
    birthday: undefined,
    terms_and_conditions: false,
  };

  // only add a value for opt_in if the field is enabled
  if (isEnabled(SIGNUP_OPTIONAL_FIELDS.OPT_IN) && strings?.signup.opt_in) {
    initialValues.opt_in = webConfig.accounts?.opt_in_auto_select;
  }

  // Validation schema for the reset form.
  const validationSchema = object({
    first_name: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .max(50, VALIDATION_STRINGS.MAX_25_CHARACTERS)
      .matches(REGEX.NAMES, REGEX_VALIDATION_STRINGS.NAMES),
    middle_name: string(),
    last_name: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .max(50, VALIDATION_STRINGS.MAX_25_CHARACTERS)
      .matches(REGEX.NAMES, REGEX_VALIDATION_STRINGS.NAMES),
    // TEAM-14991: match Toast's validator (REGEX.EMAIL) so an address Toast will refuse at
    // checkout is caught here, where the address originates. `.trim()` only normalizes the value
    // for matching (so a stray trailing space isn't read as invalid against the anchored
    // pattern); the value the container submits is trimmed in normalizedFormValues, because
    // Formik's onSubmit receives the raw input, not yup's cast. `excludeEmptyString` keeps an
    // empty field reading "Required".
    email: string()
      .trim()
      .matches(REGEX.EMAIL, { message: VALIDATION_STRINGS.EMAIL, excludeEmptyString: true })
      .required(VALIDATION_STRINGS.REQUIRED),
    phone: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .matches(REGEX.PHONE, REGEX_VALIDATION_STRINGS.PHONE),
    password: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .min(8, VALIDATION_STRINGS.PASSWORD_LENGTH),
    invite_code: lazy(() => string().optional()),
    birthday: lazy(() =>
      (isEnabled(SIGNUP_OPTIONAL_FIELDS.BIRTHDAY)
        ? string().required(VALIDATION_STRINGS.REQUIRED)
        : string().optional()
      ).matches(
        REGEX.MM_DD_YYYY_DASHES_SLASHES,
        REGEX_VALIDATION_STRINGS.MM_DD_YYYY_DASHES_SLASHES,
      ),
    ),
    opt_in: lazy(() =>
      isEnabled(SIGNUP_OPTIONAL_FIELDS.OPT_IN) && strings?.signup.opt_in
        ? boolean().required(VALIDATION_STRINGS.REQUIRED)
        : boolean().optional(),
    ),
    terms_and_conditions: lazy(() =>
      isEnabled(SIGNUP_OPTIONAL_FIELDS.TERMS_AND_CONDITIONS) &&
      strings?.signup.accept_terms_conditions
        ? boolean().oneOf([true], VALIDATION_STRINGS.REQUIRED)
        : boolean().optional(),
    ),
  });

  // The form is "busy" for the whole submit flow (captcha fetch + register + chained login);
  // `submitting` carries it across the checkout window where Redux `loading` is cleared early.
  // The barberpole `$submitting` prop below intentionally omits `loadingCaptcha` so the spinner
  // animation keeps its pre-fix behavior (it runs during the request, not the captcha load).
  const isBusy = loading || loadingCaptcha || submitting;

  return (
    <Formik onSubmit={doSignUp} initialValues={initialValues} validationSchema={validationSchema}>
      {({ submitForm }) => (
        <Form data-testid="sign-up-form">
          <Render condition={Boolean(connectionError)}>
            <ConnectionErrorState
              variant="banner"
              data-testid="signup-connection-error"
              retryTestId="signup-connection-retry"
              isRetrying={Boolean(loading || loadingCaptcha)}
              onRetry={() => {
                void submitForm();
              }}
            />
          </Render>
          <SignupBonusBadge />
          <Row>
            <StyledFormField style={{ height: 'auto', width: '100%' }}>
              <Input
                label="First Name"
                id="first_name"
                name="first_name"
                placeholder="Enter First Name"
                type="text"
              />
            </StyledFormField>
            <input
              id="middle_name"
              placeholder="Middle Name"
              name="middle_name"
              type="text"
              className="hidden"
            />
            <StyledFormField style={{ height: 'auto', width: '100%' }}>
              <Input
                label="Last Name"
                id="last_name"
                name="last_name"
                placeholder="Enter Last Name"
                type="text"
              />
            </StyledFormField>
          </Row>

          <Row>
            <StyledFormField style={{ height: 'auto', width: '100%' }}>
              <Input label="Email" id="email" name="email" placeholder="Enter Email" type="text" />
            </StyledFormField>

            <StyledFormField style={{ height: 'auto', width: '100%' }}>
              <Input
                label="Phone Number"
                id="phone"
                name="phone"
                type="tel"
                placeholder="Enter Phone Number"
                autoComplete="phone"
              />
            </StyledFormField>
          </Row>

          <Row>
            <StyledFormField style={{ height: 'auto', width: '100%' }}>
              <Input
                label="Password"
                id="password"
                name="password"
                type="password"
                placeholder="Enter Password"
                autoComplete="new-password"
              />
            </StyledFormField>
          </Row>

          <Row>
            <StyledPhoneDisclosure>
              By providing your phone number, you consent to receive text messages from {brandName}{' '}
              for order updates & loyalty programs. Standard message rates may apply. See our{' '}
              <a target="_blank" href={LINKS_MAP.privacy} rel="noreferrer">
                Terms and Conditions
              </a>
              .
            </StyledPhoneDisclosure>
          </Row>

          <Render condition={isEnabled(SIGNUP_OPTIONAL_FIELDS.REFERRAL_CODE)}>
            <Row>
              <StyledFormField style={{ height: 'auto', width: '100%' }}>
                <Input
                  label="Referral Code"
                  id="invite_code"
                  name="invite_code"
                  type="text"
                  placeholder="Enter Referral Code (optional)"
                />
              </StyledFormField>
            </Row>
          </Render>

          <Render
            condition={Boolean(
              isEnabled(SIGNUP_OPTIONAL_FIELDS.TERMS_AND_CONDITIONS) &&
                strings?.signup.accept_terms_conditions,
            )}
          >
            <Row>
              <StyledCheckbox>
                <label
                  htmlFor="terms_and_conditions"
                  className="control control--checkbox"
                  style={{ marginTop: '5px', cursor: 'pointer' }}
                >
                  <span dangerouslySetInnerHTML={{ __html: acceptTerms }} />
                  <Input
                    label={null}
                    id="terms_and_conditions"
                    name="terms_and_conditions"
                    type="checkbox"
                  />
                  <div className="control__indicator"></div>
                </label>
              </StyledCheckbox>
            </Row>
          </Render>

          <StyledSubmitButtonRow>
            <StyledPrimaryButton
              data-css-override="StyledPrimaryButton"
              data-testid="signup-button"
              type="submit"
              disabled={isBusy}
              $submitting={loading || submitting}
              $buttonTheme="primary"
            >
              {isBusy ? 'Loading' : 'Join'}
            </StyledPrimaryButton>
          </StyledSubmitButtonRow>
        </Form>
      )}
    </Formik>
  );
}

const Row = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flexWrap: 'nowrap',
  marginBottom: '8px',
  '@sm': {
    flexDirection: 'row',
    gap: '8px',
  },
  label: {
    span: {
      a: {
        textDecoration: 'underline !important',
      },
    },
  },
});
